import { Fragment, type FC } from 'react';
import { format } from 'date-fns';

import {
  Iconsvg,
  Tag,
  Tooltip,
  TooltipBody,
  TreeCustom,
  TreeNode,
  TreeNodeButton,
  TreeNodeChildren,
  TreeNodeLabel,
} from '@wk/components-react16';

import { cn } from '@/lib/utils';
import {
  getCheckpointVersion,
  normalizeCheckpointId,
} from '@/lib/checkpointKeys';
import {
  getDaysLeftText,
  getUnscoredStatusDetailsFromMetadata,
  normalizeUnscoredType,
} from '@/lib/unscoredStatus';
import type {
  CheckpointDivision,
  CheckpointItem,
  EvaluationCheckpoint,
} from '@/types';

import CheckpointStatusIcon from '../CheckpointStatusIcon';
import {
  getCheckpointCount,
  getCheckpointDomKey,
  getCheckpointKey,
  getExceptionLabel,
  isCheckpointGroup,
  isCheckpointItem,
  shouldStackCheckpointStatusTags,
} from './EvaluationSidebar.utils';

interface EvaluationSidebarTreeProps {
  filteredCheckpoints: Record<string, CheckpointDivision>;
  isLoading: boolean;
  activeCheckpoint: EvaluationCheckpoint | null;
  versionsByCheckpointId: Map<string, Set<string>>;
  onToggleNode: (accountableKey: string, checkpoint?: CheckpointItem) => void;
  onCheckpointSelect: (checkpoint: EvaluationCheckpoint) => void;
}

const cleanReason = (value?: string) =>
  (value ?? '')
    .toString()
    .replace(/-\s*maintenance\s*$/i, '')
    .trim();

const formatDateValue = (iso?: string) => {
  if (!iso) return null;

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  return format(date, 'yyyy-MM-dd HH:mm');
};

const EvaluationSidebarTree: FC<EvaluationSidebarTreeProps> = ({
  filteredCheckpoints,
  isLoading,
  activeCheckpoint,
  versionsByCheckpointId,
  onToggleNode,
  onCheckpointSelect,
}) => {
  const renderExceptionTag = (checkpoint: EvaluationCheckpoint) => {
    const domKey = getCheckpointDomKey(checkpoint);
    const exceptionLabel = getExceptionLabel(checkpoint);

    if (checkpoint.hasException || checkpoint.status === 'EXCEPTION') {
      return (
        <>
          <span
            id={`exception-${domKey}`}
            className="evaluationSidebar__statusTag"
          >
            <Tag color="red">{exceptionLabel}</Tag>
          </span>
          <Tooltip targetSelector={`#exception-${domKey}`}>
            <TooltipBody slot="tooltipBody">
              Checkpoint is not scored due to approved exception
            </TooltipBody>
          </Tooltip>
        </>
      );
    }

    if (
      checkpoint.metadata.totalExemptedAssessments &&
      checkpoint.metadata.totalExemptedAssessments > 0
    ) {
      return (
        <>
          <span
            id={`exempted-${domKey}`}
            className="evaluationSidebar__statusTag evaluationSidebar__statusPill relative ml-auto flex h-4 w-max flex-shrink-0 items-center overflow-hidden rounded-lg bg-red-400 px-2 text-[0.6875rem] font-medium tracking-[.0625rem] text-white"
          >
            <span className="z-10">{exceptionLabel}</span>
            <span
              className={cn(['absolute bottom-0 left-0 top-0 z-0 bg-red-500'])}
              style={{
                width: `${(checkpoint.metadata.totalExemptedAssessments /
                  checkpoint.metadata.totalAssessments) *
                  100}%`,
              }}
            />
          </span>
          <Tooltip targetSelector={`#exempted-${domKey}`}>
            <TooltipBody slot="tooltipBody">
              <span>
                Checkpoint is scored for{' '}
                {checkpoint.metadata.totalAssessments -
                  checkpoint.metadata.totalExemptedAssessments}{' '}
                of {checkpoint.metadata.totalAssessments} assessment
                {checkpoint.metadata.totalAssessments > 1 ? 's' : ''}.<br />
                {checkpoint.metadata.totalExemptedAssessments} assessment
                {checkpoint.metadata.totalExemptedAssessments > 1 ? 's ' : ' '}
                {checkpoint.metadata.totalExemptedAssessments > 1
                  ? 'are'
                  : 'is'}{' '}
                exempted.
              </span>
            </TooltipBody>
          </Tooltip>
        </>
      );
    }

    return null;
  };

  const renderPreviewTag = (checkpoint: EvaluationCheckpoint) => {
    const domKey = getCheckpointDomKey(checkpoint);

    if (
      checkpoint.metadata.isScored &&
      checkpoint.metadata.scorePercent &&
      checkpoint.metadata.scorePercent !== 1
    ) {
      return (
        <>
          <span
            id={`preview-${domKey}`}
            className="evaluationSidebar__statusTag evaluationSidebar__statusPill relative flex h-4 w-max flex-shrink-0 items-center overflow-hidden rounded-lg bg-gray-400 px-2 text-[0.6875rem] font-medium tracking-[.0625rem] text-white"
          >
            <span className="z-10">PREVIEW</span>
            <span
              className={cn([
                'absolute bottom-0 left-0 top-0 z-0 bg-[#232323]',
              ])}
              style={{
                width: `${(checkpoint.metadata.totalPreviewControls /
                  checkpoint.metadata.totalControls) *
                  100}%`,
              }}
            />
          </span>
          <Tooltip targetSelector={`#preview-${domKey}`}>
            <TooltipBody slot="tooltipBody">
              <span>
                Checkpoint is scored for{' '}
                {checkpoint.metadata.totalControls -
                  checkpoint.metadata.totalPreviewControls}{' '}
                of {checkpoint.metadata.totalControls} control
                {checkpoint.metadata.totalControls > 1 ? 's' : ''}.{' '}
                {checkpoint.metadata.totalPreviewControls} control
                {checkpoint.metadata.totalPreviewControls > 1 ? 's' : ''}
                {checkpoint.metadata.totalPreviewControls > 1 ? 'are' : 'is'}{' '}
                in preview.
              </span>
            </TooltipBody>
          </Tooltip>
        </>
      );
    }

    if (!checkpoint.metadata.isScored) {
      const unscored = getUnscoredStatusDetailsFromMetadata(checkpoint.metadata);
      const unscoredType = normalizeUnscoredType(unscored?.unscoredType);
      const daysLeftText = getDaysLeftText(unscored?.endDate);
      const maintenanceLabel = daysLeftText
        ? `MAINTENANCE - ${daysLeftText}`
        : 'MAINTENANCE';
      const previewLabel = daysLeftText
        ? `PREVIEW - ${daysLeftText}`
        : 'PREVIEW';
      const isMaintenance =
        unscoredType === 'maintenance' || checkpoint.metadata.isMaintenance;

      if (isMaintenance) {
        const reason = unscored?.reason || checkpoint.metadata.maintenance?.note;
        const eventTime =
          unscored?.eventTime || checkpoint.metadata.maintenance?.eventTime;
        const startDate = unscored?.startDate;
        const endDate = unscored?.endDate;

        const reasonText = cleanReason(reason);
        const startText = formatDateValue(startDate);
        const endText = formatDateValue(endDate);
        const updatedText = eventTime ? formatDateValue(eventTime) : null;

        return (
          <>
            <span
              id={`maintenance-${domKey}`}
              className="evaluationSidebar__statusTag"
            >
              <Tag color="orange">{maintenanceLabel}</Tag>
            </span>
            <Tooltip targetSelector={`#maintenance-${domKey}`}>
              <TooltipBody slot="tooltipBody">
                <div
                  className="flex flex-col gap-1"
                  style={{
                    maxWidth: '420px',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {reasonText ? (
                    <div>
                      <span className="font-semibold">Reason:</span> {reasonText}
                    </div>
                  ) : null}
                  {startText ? (
                    <div>
                      <span className="font-semibold">Start:</span> {startText}
                    </div>
                  ) : null}
                  {endText ? (
                    <div>
                      <span className="font-semibold">End:</span> {endText}
                    </div>
                  ) : null}
                  {updatedText ? (
                    <div>
                      <span className="font-semibold">Updated:</span>{' '}
                      {updatedText}
                    </div>
                  ) : null}
                  {!reasonText && !startText && !endText && !updatedText ? (
                    <div>No details available.</div>
                  ) : null}
                </div>
              </TooltipBody>
            </Tooltip>
          </>
        );
      }

      const reasonText = cleanReason(unscored?.reason || '');
      const startText = formatDateValue(unscored?.startDate);
      const endText = formatDateValue(unscored?.endDate);

      return (
        <>
          <span
            id={`preview-${domKey}`}
            className="evaluationSidebar__statusTag"
          >
            <Tag color="gray" contrastMode="light">
              {previewLabel}
            </Tag>
          </span>
          <Tooltip targetSelector={`#preview-${domKey}`}>
            <TooltipBody slot="tooltipBody">
              <div
                className="flex flex-col gap-1"
                style={{
                  maxWidth: '420px',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {reasonText ? (
                  <div>
                    <span className="font-semibold">Reason:</span> {reasonText}
                  </div>
                ) : (
                  <div>Checkpoint is not scored since it is in preview.</div>
                )}
                {startText ? (
                  <div>
                    <span className="font-semibold">Start:</span> {startText}
                  </div>
                ) : null}
                {endText ? (
                  <div>
                    <span className="font-semibold">End:</span> {endText}
                  </div>
                ) : null}
                {!reasonText && !startText && !endText ? (
                  <div>It will be included in an upcoming release.</div>
                ) : null}
              </div>
            </TooltipBody>
          </Tooltip>
        </>
      );
    }

    return null;
  };

  const renderCheckpointNode = (checkpoint: EvaluationCheckpoint) => {
    const checkpointKey = getCheckpointKey(checkpoint);
    const checkpointVersion = getCheckpointVersion(checkpoint.metadata);
    const hasMultipleVersions =
      (versionsByCheckpointId.get(
        normalizeCheckpointId(checkpoint.metadata.id),
      )?.size ?? 0) > 1;
    const shouldShowVersion = Boolean(checkpointVersion) && hasMultipleVersions;
    const shouldStackStatusTags = shouldStackCheckpointStatusTags(checkpoint);
    const isSelected = activeCheckpoint
      ? getCheckpointKey(activeCheckpoint) === checkpointKey
      : false;

    return (
      <TreeNode key={checkpointKey}>
        <TreeNodeLabel
          slot="treeNodeLabel"
          isSelected={isSelected}
          onLabelClick={() => onCheckpointSelect(checkpoint)}
        >
          <div className="flex items-start gap-2">
            <span className="shrink-0 pt-0.5">
              <CheckpointStatusIcon checkpoint={checkpoint} />
            </span>
            <div className="flex min-w-0 flex-1 flex-wrap items-start gap-x-2 gap-y-1">
              <div className="evaluationSidebar__checkpointTitle flex-1">
                {checkpoint.metadata.displayName}
                {shouldShowVersion ? (
                  <span className="ml-1 text-xs text-gray-500">
                    v{checkpointVersion}
                  </span>
                ) : null}
              </div>
              <div
                className={cn(
                  'ml-auto flex max-w-full shrink-0 items-center justify-end gap-1',
                  shouldStackStatusTags &&
                    'evaluationSidebar__statusGroup--stacked flex-col items-end gap-1',
                )}
              >
                {renderExceptionTag(checkpoint)}
                {renderPreviewTag(checkpoint)}
              </div>
            </div>
          </div>
        </TreeNodeLabel>
      </TreeNode>
    );
  };

  return (
    <TreeCustom isLoading={isLoading}>
      {Object.entries(filteredCheckpoints).map(
        ([accountableKey, checkpointDivision]) => (
          <TreeNode
            key={accountableKey}
            isExpanded={checkpointDivision.isActive}
            onExpand={() => onToggleNode(accountableKey)}
            onCollapse={() => onToggleNode(accountableKey)}
          >
            <TreeNodeButton
              onButtonClick={() => onToggleNode(accountableKey)}
              slot="treeNodeButton"
            >
              <span
                className={cn([
                  'transition-transform duration-200',
                  checkpointDivision.isActive && 'rotate-90',
                ])}
              >
                <Iconsvg name="chevron-right" size={12} />
              </span>
            </TreeNodeButton>
            <TreeNodeLabel
              isSelected={checkpointDivision.isActive}
              onLabelClick={() => onToggleNode(accountableKey)}
              slot="treeNodeLabel"
            >
              {checkpointDivision.title} ({getCheckpointCount(checkpointDivision)})
            </TreeNodeLabel>
            <TreeNodeChildren slot="treeNodeChildren">
              {checkpointDivision.checkpoints.map((checkpoint) => {
                if (isCheckpointGroup(checkpoint)) {
                  return (
                    <TreeNode
                      key={checkpoint.groupName}
                      isExpanded={checkpoint.isActive}
                      onExpand={() => onToggleNode(accountableKey, checkpoint)}
                      onCollapse={() => onToggleNode(accountableKey, checkpoint)}
                    >
                      <TreeNodeButton
                        onButtonClick={() =>
                          onToggleNode(accountableKey, checkpoint)
                        }
                        slot="treeNodeButton"
                      >
                        <span
                          className={cn([
                            'transition-transform duration-200',
                            checkpoint.isActive && 'rotate-90',
                          ])}
                        >
                          <Iconsvg name="chevron-right" size={12} />
                        </span>
                      </TreeNodeButton>
                      <TreeNodeLabel
                        isSelected={checkpoint.isActive}
                        onLabelClick={() =>
                          onToggleNode(accountableKey, checkpoint)
                        }
                        slot="treeNodeLabel"
                      >
                        {checkpoint.groupName}
                      </TreeNodeLabel>
                      <TreeNodeChildren slot="treeNodeChildren">
                        {checkpoint.checkpoints.map((groupCheckpoint) => (
                          <Fragment
                            key={getCheckpointDomKey(groupCheckpoint)}
                          >
                            {renderCheckpointNode(groupCheckpoint)}
                          </Fragment>
                        ))}
                      </TreeNodeChildren>
                    </TreeNode>
                  );
                }

                if (isCheckpointItem(checkpoint)) {
                  return (
                    <Fragment key={getCheckpointDomKey(checkpoint)}>
                      {renderCheckpointNode(checkpoint)}
                    </Fragment>
                  );
                }

                return null;
              })}
            </TreeNodeChildren>
          </TreeNode>
        ),
      )}
    </TreeCustom>
  );
};

export default EvaluationSidebarTree;
